import type { ChartPalette } from '@shared/chart';
import { MANIA_LANE_COUNT, type ManiaLane, type ManiaNote } from '@shared/maniaNote';

/** Vertical padding above the first beat shown. */
export const EDITOR_TOP_PAD = 16;
/** Space reserved below the playhead for the now-line + lane key caps. */
export const EDITOR_BOTTOM_PAD = 46;

/**
 * Everything the beat ↔ pixel maths needs. The page owns these values
 * (scroll position + zoom) and threads them through both the renderer
 * and the pointer handlers so placement and drawing never disagree.
 */
export interface EditorGeometry {
  width: number;
  height: number;
  /** Beat sitting on the playhead (bottom of the lane area). */
  scrollBeat: number;
  /** Vertical zoom: screen pixels per beat. */
  pxPerBeat: number;
}

/** Y of the playhead / "now" line — where `scrollBeat` sits. */
export function editorPlayheadY(geo: EditorGeometry): number {
  return geo.height - EDITOR_BOTTOM_PAD;
}

/** Beats are time-up: later beats sit higher on the canvas. */
export function editorBeatToY(beat: number, geo: EditorGeometry): number {
  return editorPlayheadY(geo) - (beat - geo.scrollBeat) * geo.pxPerBeat;
}

export function editorYToBeat(y: number, geo: EditorGeometry): number {
  return geo.scrollBeat + (editorPlayheadY(geo) - y) / geo.pxPerBeat;
}

export function editorLaneWidth(geo: EditorGeometry): number {
  return geo.width / MANIA_LANE_COUNT;
}

export function editorLaneCenterX(lane: number, geo: EditorGeometry): number {
  return editorLaneWidth(geo) * (lane + 0.5);
}

/** Lane under an x pixel, or null if outside the lane area. */
export function editorLaneFromX(x: number, geo: EditorGeometry): ManiaLane | null {
  const lane = Math.floor(x / editorLaneWidth(geo));
  if (lane < 0 || lane >= MANIA_LANE_COUNT) {
    return null;
  }
  return lane as ManiaLane;
}

/** Snap a raw beat to the nearest grid subdivision (`divisions` per beat). */
export function snapBeatToGrid(beat: number, divisions: number): number {
  if (divisions <= 0) {
    return beat;
  }
  return Math.round(beat * divisions) / divisions;
}

/** How many beats are visible above the playhead at the current zoom. */
export function editorVisibleBeats(geo: EditorGeometry): number {
  return (editorPlayheadY(geo) - EDITOR_TOP_PAD) / geo.pxPerBeat;
}

const TAP_HEIGHT = 14;
const TAP_INSET = 6;
/** Loop highlight colour; mirrors the CSS `--success` green (canvas can't read
 * CSS vars), matching the "playing" now-line and the Pulse loop band. */
const LOOP_GREEN = '#3bff9d';
/**
 * Width of the left-edge loop gutter — the drag-to-select strip for the loop
 * window. The page hit-tests against this so a drag starting here sets the
 * loop region instead of placing a note.
 */
export const LOOP_GUTTER_W = 18;

export interface GuitarEditorGridInput {
  width: number;
  height: number;
  notes: ReadonlyArray<ManiaNote>;
  scrollBeat: number;
  pxPerBeat: number;
  totalBeats: number;
  /** Grid subdivisions per beat (1 = on-beat, 2 = half-beat, …). */
  snap: number;
  selectedId: string | null;
  hover: { beat: number; lane: ManiaLane } | null;
  palette: ChartPalette;
  /** Per-lane key caps drawn under the playhead, e.g. ['D','F','J','K']. */
  keyLabels: ReadonlyArray<string>;
  playing: boolean;
  /** Beat window being looped, drawn as a highlighted band. */
  loopRegion: { startBeat: number; endBeat: number } | null;
  /** Sweeping playback head (beats) while looping; null when not looping. */
  loopPlayheadBeat: number | null;
}

/**
 * Flat 4-lane piano-roll renderer for the Guitar map editor. Distinct
 * from the gameplay {@link import('../guitarRenderer.js').GuitarRenderer}
 * (a perspective fretboard) because precise placement needs an
 * undistorted grid: lanes are even columns, time runs straight up the
 * canvas, and the now-line is fixed at the bottom.
 */
export class GuitarEditorGrid {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('GuitarEditorGrid: 2D canvas context unavailable');
    }
    this.ctx = ctx;
  }

  draw(input: GuitarEditorGridInput): void {
    const { width, height } = input;
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const backingW = Math.round(width * dpr);
    const backingH = Math.round(height * dpr);
    if (this.canvas.width !== backingW) {
      this.canvas.width = backingW;
    }
    if (this.canvas.height !== backingH) {
      this.canvas.height = backingH;
    }
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const geo: EditorGeometry = {
      width,
      height,
      scrollBeat: input.scrollBeat,
      pxPerBeat: input.pxPerBeat,
    };

    this.drawBackground(input, geo);
    this.drawGridLines(input, geo);
    if (input.loopRegion) {
      this.drawLoopBand(input, geo);
    }
    this.drawLaneSeparators(geo);
    this.drawNotes(input, geo);
    if (input.hover) {
      this.drawHoverGhost(input, geo);
    }
    this.drawLoopGutter(input, geo);
    if (input.loopPlayheadBeat !== null) {
      this.drawLoopPlayhead(input.loopPlayheadBeat, geo);
    }
    this.drawPlayhead(input, geo);
    this.drawKeyCaps(input, geo);
  }

  /** Translucent fill over the looped beat window across the lanes. */
  private drawLoopBand(input: GuitarEditorGridInput, geo: EditorGeometry): void {
    const region = input.loopRegion;
    if (!region) {
      return;
    }
    const { y, h } = loopBandRect(region, geo);
    this.ctx.fillStyle = withAlpha(LOOP_GREEN, 0.09);
    this.ctx.fillRect(0, y, geo.width, h);
  }

  /**
   * The left-edge loop gutter: the drag-to-select strip. Always drawn (a faint
   * rail signals it's interactive); when a region exists its segment is filled
   * solid as a grabbable handle so the loop bounds read clearly.
   */
  private drawLoopGutter(input: GuitarEditorGridInput, geo: EditorGeometry): void {
    const ctx = this.ctx;
    const playheadY = editorPlayheadY(geo);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.fillRect(0, 0, LOOP_GUTTER_W, playheadY);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(LOOP_GUTTER_W + 0.5, 0);
    ctx.lineTo(LOOP_GUTTER_W + 0.5, playheadY);
    ctx.stroke();

    const region = input.loopRegion;
    if (region) {
      const { y, h } = loopBandRect(region, geo);
      ctx.fillStyle = withAlpha(LOOP_GREEN, 0.85);
      ctx.fillRect(0, y, LOOP_GUTTER_W, h);
    }
  }

  /** The sweeping playback head while looping, drawn as a bright line. */
  private drawLoopPlayhead(beat: number, geo: EditorGeometry): void {
    const ctx = this.ctx;
    const y = editorBeatToY(beat, geo);
    ctx.strokeStyle = LOOP_GREEN;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(geo.width, y + 0.5);
    ctx.stroke();
  }

  private drawBackground(input: GuitarEditorGridInput, geo: EditorGeometry): void {
    const ctx = this.ctx;
    const grad = ctx.createLinearGradient(0, 0, 0, geo.height);
    grad.addColorStop(0, input.palette.bg);
    grad.addColorStop(1, input.palette.bgAccent);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, geo.width, geo.height);

    // Dim the region past the end of the chart so the author can see
    // where the playable window stops.
    const endY = editorBeatToY(input.totalBeats, geo);
    if (endY > 0) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.fillRect(0, 0, geo.width, Math.min(endY, geo.height));
    }
  }

  private drawGridLines(input: GuitarEditorGridInput, geo: EditorGeometry): void {
    const ctx = this.ctx;
    const topBeat = input.scrollBeat + editorVisibleBeats(geo) + 1;
    const firstTick = Math.max(0, Math.floor(input.scrollBeat * input.snap));
    const lastTick = Math.ceil(topBeat * input.snap);
    ctx.lineWidth = 1;
    for (let tick = firstTick; tick <= lastTick; tick += 1) {
      const beat = tick / input.snap;
      if (beat > input.totalBeats + 0.001) {
        continue;
      }
      const y = editorBeatToY(beat, geo);
      if (y < -2 || y > geo.height + 2) {
        continue;
      }
      const isWholeBeat = Math.abs(beat - Math.round(beat)) < 1e-6;
      const isBar = isWholeBeat && Math.round(beat) % 4 === 0;
      if (isBar) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
      } else if (isWholeBeat) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
      } else {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
      }
      ctx.beginPath();
      ctx.moveTo(0, y + 0.5);
      ctx.lineTo(geo.width, y + 0.5);
      ctx.stroke();
      if (isBar) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.font = '10px var(--font-mono, monospace)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${Math.round(beat / 4)}`, 4, y - 2);
      }
    }
  }

  private drawLaneSeparators(geo: EditorGeometry): void {
    const ctx = this.ctx;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    for (let lane = 1; lane < MANIA_LANE_COUNT; lane += 1) {
      const x = editorLaneWidth(geo) * lane;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, editorPlayheadY(geo));
      ctx.stroke();
    }
  }

  private drawNotes(input: GuitarEditorGridInput, geo: EditorGeometry): void {
    const ctx = this.ctx;
    const laneW = editorLaneWidth(geo);
    const noteW = laneW - TAP_INSET * 2;
    const minVisible = input.scrollBeat - 1;
    const maxVisible = input.scrollBeat + editorVisibleBeats(geo) + 2;
    for (const note of input.notes) {
      const endBeat = note.holdEndBeat ?? note.beat;
      if (endBeat < minVisible || note.beat > maxVisible) {
        continue;
      }
      const selected = note.id === input.selectedId;
      const headY = editorBeatToY(note.beat, geo);
      const x = editorLaneCenterX(note.lane, geo) - noteW / 2;

      if (note.holdEndBeat !== undefined) {
        const tailY = editorBeatToY(note.holdEndBeat, geo);
        ctx.fillStyle = withAlpha(input.palette.padPerfect, 0.32);
        ctx.fillRect(x + noteW * 0.2, tailY, noteW * 0.6, headY - tailY);
      }

      ctx.fillStyle = selected ? input.palette.player : input.palette.pad;
      ctx.strokeStyle = selected ? '#ffffff' : 'rgba(0, 0, 0, 0.4)';
      ctx.lineWidth = selected ? 2 : 1;
      roundRect(ctx, x, headY - TAP_HEIGHT / 2, noteW, TAP_HEIGHT, 4);
      ctx.fill();
      ctx.stroke();

      if (note.dualPartnerLane !== undefined) {
        const px = editorLaneCenterX(note.dualPartnerLane, geo) - noteW / 2;
        ctx.fillStyle = withAlpha(input.palette.player, 0.85);
        roundRect(ctx, px, headY - TAP_HEIGHT / 2, noteW, TAP_HEIGHT, 4);
        ctx.fill();
      }
    }
  }

  private drawHoverGhost(input: GuitarEditorGridInput, geo: EditorGeometry): void {
    const hover = input.hover;
    if (!hover) {
      return;
    }
    const ctx = this.ctx;
    const laneW = editorLaneWidth(geo);
    const noteW = laneW - TAP_INSET * 2;
    const y = editorBeatToY(hover.beat, geo);
    const x = editorLaneCenterX(hover.lane, geo) - noteW / 2;
    ctx.fillStyle = withAlpha(input.palette.player, 0.25);
    ctx.strokeStyle = withAlpha(input.palette.player, 0.7);
    ctx.lineWidth = 1;
    roundRect(ctx, x, y - TAP_HEIGHT / 2, noteW, TAP_HEIGHT, 4);
    ctx.fill();
    ctx.stroke();
  }

  private drawPlayhead(input: GuitarEditorGridInput, geo: EditorGeometry): void {
    const ctx = this.ctx;
    const y = editorPlayheadY(geo);
    ctx.strokeStyle = input.playing ? '#3bff9d' : input.palette.player;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(geo.width, y + 0.5);
    ctx.stroke();

    ctx.fillStyle = ctx.strokeStyle;
    ctx.font = '10px var(--font-mono, monospace)';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`beat ${input.scrollBeat.toFixed(2)}`, geo.width - 4, y - 2);
  }

  private drawKeyCaps(input: GuitarEditorGridInput, geo: EditorGeometry): void {
    const ctx = this.ctx;
    const laneW = editorLaneWidth(geo);
    const y = geo.height - EDITOR_BOTTOM_PAD / 2;
    ctx.font = '600 13px var(--font-display, sans-serif)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let lane = 0; lane < MANIA_LANE_COUNT; lane += 1) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
      ctx.fillText(input.keyLabels[lane] ?? '', laneW * (lane + 0.5), y);
    }
  }
}

/** Pixel top + height of a loop window's band (beats run up the canvas). */
function loopBandRect(
  region: { startBeat: number; endBeat: number },
  geo: EditorGeometry,
): { y: number; h: number } {
  const topY = editorBeatToY(region.endBeat, geo);
  const bottomY = editorBeatToY(region.startBeat, geo);
  return { y: Math.min(topY, bottomY), h: Math.abs(bottomY - topY) };
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Apply an alpha to a #RRGGBB hex colour, returning an rgba() string. */
function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) {
    return hex;
  }
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
